import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import LastUpdatedBadge from '@/components/common/LastUpdatedBadge';
import RefreshButton from '@/components/common/RefreshButton';
import { useForceRefresh } from '@/hooks/useForceRefresh';
import { useCampaignAnalysisQuery } from '@/hooks/queries/useAnalyticsQueries';

interface CampaignRefreshControlsProps {
  brandId: string;
  /** Dummy users have no cached analytics to force-refresh. */
  enabled: boolean;
}

/**
 * Last-updated stamp and force-refresh control for the brand dashboard. These
 * live in the page header beside the title, so they sit outside CampaignSection
 * and share its analytics query through the react-query cache.
 */
const CampaignRefreshControls: React.FC<CampaignRefreshControlsProps> = ({
  brandId,
  enabled,
}) => {
  const queryClient = useQueryClient();
  const { data } = useCampaignAnalysisQuery(brandId, enabled && !!brandId);

  const { refresh, isRefreshing, secondsRemaining } = useForceRefresh(
    // The panel's KPIs, top posts and reach are campaign-scoped queries; the
    // campaign list read caches its EMV + engagement rate, so it refreshes too.
    () =>
      Promise.all(
        [['analytics', 'brand'], ['analytics', 'campaign'], ['campaigns']].map(
          (queryKey) =>
            queryClient.refetchQueries({ queryKey, type: 'active' }),
        ),
      ),
    `dashboard-campaign-section:${brandId}`,
  );

  if (!enabled || !brandId) return null;

  return (
    <div
      data-testid="campaign-refresh-controls"
      className="flex items-center gap-6"
    >
      {/* Figma's status-pill and refresh-button, overriding the shared styles. */}
      <LastUpdatedBadge
        lastRefreshedAt={data?.lastRefreshedAt}
        style={{
          width: 'auto',
          height: 'auto',
          padding: '10px 20px',
          backgroundColor: 'transparent',
          color: '#5C5D70',
          fontWeight: 500,
          lineHeight: '19px',
          letterSpacing: '-0.01em',
        }}
      />
      <RefreshButton
        onRefresh={refresh}
        isRefreshing={isRefreshing}
        secondsRemaining={secondsRemaining}
        className="!gap-2 !rounded-full !border-0 !bg-white !px-3 !py-2 font-header !text-[15px] !font-semibold !leading-5 !tracking-[-0.15px] !text-[#5C5D70]"
      />
    </div>
  );
};

export default CampaignRefreshControls;
